import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axiosInstance from '../AxiosInstance';
import { trackProductEdited } from '../services/analytics';
import { Toast, Field, PageHeader, LoadingSpinner } from './v2-utils';
import { getBestProductImage } from '../utils/productImages';
import '../styles/v2.css';

async function compressImage(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = e => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX = 1024;
        let { width, height } = img;
        if (width > MAX || height > MAX) {
          if (width > height) { height = Math.round(height * MAX / width); width = MAX; }
          else { width = Math.round(width * MAX / height); height = MAX; }
        }
        canvas.width = width; canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        let dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        if (dataUrl.length > 10 * 1024 * 1024) dataUrl = canvas.toDataURL('image/jpeg', 0.5);
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function EditProductV2() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const addImageMode = searchParams.get('addImage') === 'true';

  const fileInputRef   = useRef(null);
  const cameraInputRef = useRef(null);

  const [productName, setProductName]   = useState('');
  const [brand, setBrand]               = useState('');
  const [variety, setVariety]           = useState('');
  const [size, setSize]                 = useState('');
  const [brandSuggestions, setBrandSuggestions] = useState([]);
  const [currentImage, setCurrentImage] = useState(null);   /* from DB */
  const [capturedImage, setCapturedImage] = useState(null); /* newly chosen */
  const [imagePreview, setImagePreview] = useState(null);
  const [imageChanged, setImageChanged] = useState(false);
  const [removeImage, setRemoveImage]   = useState(false);
  const [isOnline, setIsOnline]         = useState(navigator.onLine);
  const [loading, setLoading]           = useState(true);
  const [saving, setSaving]             = useState(false);
  const [toast, setToast]               = useState(null);

  useEffect(() => {
    const up = () => setIsOnline(true), down = () => setIsOnline(false);
    window.addEventListener('online', up); window.addEventListener('offline', down);
    return () => { window.removeEventListener('online', up); window.removeEventListener('offline', down); };
  }, []);

  useEffect(() => {
    if (!isOnline) { setToast({ message: 'You are offline. Cannot load product data.', severity: 'warning' }); setLoading(false); return; }
    axiosInstance.get(`/products/${id}`)
      .then(r => {
        const p = r.data;
        setProductName(p.product_name || '');
        setVariety(p.variety || '');
        setBrand(p.brand || '');
        setSize(p.size || p.description || '');
        const img = getBestProductImage(p, 'compressed');
        if (img) { setCurrentImage(img); setImagePreview(img); }
        if (addImageMode && !img) setToast({ message: 'Add an image using the buttons below.', severity: 'info' });
      })
      .catch(() => setToast({ message: 'Failed to load product data.', severity: 'error' }))
      .finally(() => setLoading(false));
  }, [id, addImageMode, isOnline]);

  /* brand suggestions */
  useEffect(() => {
    if (productName.length > 1 && isOnline) {
      axiosInstance.get(`/products/brands/${encodeURIComponent(productName)}`)
        .then(r => setBrandSuggestions(r.data.brands || []))
        .catch(() => setBrandSuggestions([]));
    } else setBrandSuggestions([]);
  }, [productName, isOnline]);

  const handleFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) { setToast({ message: 'Please select a valid image file.', severity: 'error' }); return; }
    if (file.size > 5 * 1024 * 1024) { setToast({ message: 'Image too large (max 5MB). Choose a smaller image.', severity: 'error' }); return; }
    try {
      const compressed = await compressImage(file);
      setCapturedImage(compressed); setImagePreview(compressed); setImageChanged(true); setRemoveImage(false);
    } catch { setToast({ message: 'Failed to process image.', severity: 'error' }); }
  };

  const handleCancelImageChange = () => {
    setCapturedImage(null); setImagePreview(currentImage); setImageChanged(false); setRemoveImage(false);
  };

  const handleRemoveImage = () => {
    setCapturedImage(null); setImagePreview(null); setImageChanged(true); setRemoveImage(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isOnline) { setToast({ message: 'Cannot save while offline.', severity: 'error' }); return; }
    if (capturedImage && capturedImage.length > 10 * 1024 * 1024) { setToast({ message: 'Image is too large. Try a smaller one.', severity: 'error' }); return; }
    setSaving(true);
    try {
      const payload = {
        product_name: productName.trim(),
        variety: variety.trim(),
        brand: brand.trim() || null,
        size: size.trim(),
        ...(imageChanged ? { image: removeImage ? null : capturedImage } : {}),
      };
      await axiosInstance.put(`/products/${id}`, payload);
      trackProductEdited(id);
      setToast({ message: 'Product updated successfully!', severity: 'success' });
      setTimeout(() => navigate('/new/dashboard/products/view'), 1500);
    } catch (err) {
      const status = err.response?.status;
      if (!err.response) setToast({ message: 'Network error. Check your connection.', severity: 'error' });
      else if (status === 413) setToast({ message: 'Image too large. Try a smaller image.', severity: 'error' });
      else if (status === 408 || err.code === 'ECONNABORTED') setToast({ message: 'Upload timed out. Try a smaller image.', severity: 'error' });
      else setToast({ message: err.response?.data?.error || 'Failed to update product.', severity: 'error' });
    } finally { setSaving(false); }
  };

  if (loading) return <LoadingSpinner message="Loading product…" />;

  return (
    <div style={{ maxWidth: 680, margin: '0 auto' }}>
      <PageHeader title="Edit Product" onBack={() => navigate('/new/dashboard/products/view')} />

      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Details card */}
          <div className="v2-card" style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 18 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Product Details</p>

            <Field label="Product Name *">
              <input className="v2-input" type="text" value={productName} onChange={e => setProductName(e.target.value)} required />
            </Field>

            <Field label="Brand" hint="Leave blank if product has no brand">
              <input className="v2-input" type="text" value={brand} onChange={e => setBrand(e.target.value)} placeholder="e.g. Coca-Cola, Unilever…" />
              {brandSuggestions.length > 0 && (
                <div style={{ marginTop: 8 }}>
                  <p style={{ fontSize: 11, color: '#94a3b8', margin: '0 0 6px' }}>Existing brands:</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    {brandSuggestions.map(b => (
                      <button key={b} type="button" onClick={() => setBrand(b)}
                        style={{ padding: '4px 10px', borderRadius: 99, border: `1.5px solid ${brand === b ? '#10b981' : '#e2e8f0'}`, background: brand === b ? '#f0fdf4' : 'white', color: brand === b ? '#065f46' : '#475569', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                        {b}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </Field>

            <div className="v2-sale-price-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Variety" hint="e.g. Gala, Granny Smith">
                <input className="v2-input" type="text" value={variety} onChange={e => setVariety(e.target.value)} placeholder="Optional" />
              </Field>
              <Field label="Size / Description" hint="e.g. 300ml, 500g">
                <input className="v2-input" type="text" value={size} onChange={e => setSize(e.target.value)} placeholder="Optional" />
              </Field>
            </div>
          </div>

          {/* Image card */}
          <div className="v2-card" style={{ padding: 24 }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 16px' }}>Product Image</p>

            {addImageMode && !currentImage && (
              <div style={{ marginBottom: 16, padding: '12px 16px', borderRadius: 10, background: '#eff6ff', border: '1px solid #bfdbfe' }}>
                <p style={{ margin: 0, fontSize: 13, color: '#1d4ed8', fontWeight: 500 }}>Add Image Mode — use the buttons below to capture or upload a photo.</p>
              </div>
            )}

            {/* Image preview */}
            {imagePreview ? (
              <div style={{ position: 'relative', marginBottom: 16, borderRadius: 12, overflow: 'hidden', border: '1.5px solid #e2e8f0' }}>
                <img src={imagePreview} alt="Preview" style={{ width: '100%', maxHeight: 240, objectFit: 'contain', background: '#f8fafc', display: 'block' }} />
              </div>
            ) : (
              <div style={{ marginBottom: 16, height: 120, borderRadius: 12, border: '1.5px dashed #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc', color: '#94a3b8', fontSize: 13 }}>
                No image
              </div>
            )}

            {/* Hidden inputs */}
            <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />
            <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={e => handleFile(e.target.files?.[0])} />

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button type="button" onClick={() => cameraInputRef.current?.click()}
                style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 14px', borderRadius: 9, border: '1.5px solid #e2e8f0', background: 'white', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M6.827 6.175A2.31 2.31 0 015.186 7.23c-.38.054-.757.112-1.134.175C2.999 7.58 2.25 8.507 2.25 9.574V18a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9.574c0-1.067-.75-1.994-1.802-2.169a47.865 47.865 0 00-1.134-.175 2.31 2.31 0 01-1.64-1.055l-.822-1.316a2.192 2.192 0 00-1.736-1.039 48.774 48.774 0 00-5.232 0 2.192 2.192 0 00-1.736 1.039l-.821 1.316z" /><path strokeLinecap="round" strokeLinejoin="round" d="M16.5 12.75a4.5 4.5 0 11-9 0 4.5 4.5 0 019 0zM18.75 10.5h.008v.008h-.008V10.5z" /></svg>
                {capturedImage ? 'Retake Photo' : 'Take Photo'}
              </button>
              <button type="button" onClick={() => fileInputRef.current?.click()}
                style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 14px', borderRadius: 9, border: '1.5px solid #e2e8f0', background: 'white', color: '#334155', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                <svg style={{ width: 14, height: 14 }} fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5z" /></svg>
                Upload from Library
              </button>
              {imageChanged && (
                <button type="button" onClick={handleCancelImageChange}
                  style={{ padding: '9px 14px', borderRadius: 9, border: '1.5px solid #e2e8f0', background: 'white', color: '#64748b', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Cancel Change
                </button>
              )}
              {(currentImage || capturedImage) && !removeImage && (
                <button type="button" onClick={handleRemoveImage}
                  style={{ padding: '9px 14px', borderRadius: 9, border: '1.5px solid #fecaca', background: '#fef2f2', color: '#ef4444', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  Remove Image
                </button>
              )}
            </div>
          </div>

          <button type="submit" className="v2-btn-submit" disabled={saving || !isOnline} style={{ fontSize: 15 }}>
            {saving ? 'Saving…' : !isOnline ? 'Cannot save while offline' : 'Save Changes'}
          </button>
        </div>
      </form>
      {toast && <Toast {...toast} onClose={() => setToast(null)} />}
    </div>
  );
}
